// Client-side document snippets for vault card previews (PDF page, spreadsheets, office, archives).
import JSZip from "jszip";
import * as XLSX from "xlsx";

import { fetchVaultItemAsFile, fetchVaultItemPreviewUrl, type VaultItem } from "../../morrowVault";
import {
  isArchiveFile,
  isHeicMime,
  isOfficeFile,
  isSpreadsheetFile,
  resolveVaultMimeType,
} from "./vaultShared";

const SNIPPET_MAX_CHARS = 2_000;
const ARCHIVE_LISTING_MAX_ENTRIES = 40;

let unpdfReady: Promise<void> | null = null;

async function ensureUnpdf(): Promise<typeof import("unpdf")> {
  const mod = await import("unpdf");
  if (!unpdfReady) {
    unpdfReady = mod.definePDFJSModule(() => import("unpdf/pdfjs"));
  }
  await unpdfReady;
  return mod;
}

function trimSnippet(text: string, maxChars = SNIPPET_MAX_CHARS): string | null {
  const trimmed = text.replace(/\s+/g, " ").trim();
  return trimmed.length > 0 ? trimmed.slice(0, maxChars) : null;
}

function extractXmlTextTags(xml: string, tag: string): string[] {
  const parts: string[] = [];
  const re = new RegExp(`<${tag}[^>]*>([^<]*)</${tag}>`, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml)) !== null && parts.join(" ").length < SNIPPET_MAX_CHARS) {
    const value = match[1]?.trim();
    if (value) parts.push(value);
  }
  return parts;
}

function extractPrintableStrings(bytes: ArrayBuffer, maxChars = SNIPPET_MAX_CHARS): string | null {
  const view = new Uint8Array(bytes);
  const chunks: string[] = [];
  let current = "";
  for (let i = 0; i < view.length && current.length < maxChars; i++) {
    const byte = view[i]!;
    if (byte >= 32 && byte <= 126) {
      current += String.fromCharCode(byte);
    } else if (current.length >= 4) {
      chunks.push(current);
      current = "";
    } else {
      current = "";
    }
  }
  if (current.length >= 4) chunks.push(current);
  return trimSnippet(chunks.join(" "));
}

/** Render the first page of a PDF as a data URL thumbnail. */
export async function renderVaultPdfThumbnail(item: VaultItem): Promise<string | null> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return null;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const { renderPageAsImage } = await ensureUnpdf();
    return await renderPageAsImage(bytes, 1, {
      toDataURL: true,
      scale: 1.2,
      width: 480,
    });
  } catch (err) {
    console.warn("[vault] pdf thumbnail error", err);
    return null;
  }
}

/** Extract readable text from a PDF without rendering (canvas-free fallback). */
export async function extractPdfTextSnippet(
  item: VaultItem,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return null;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const { extractText, getDocumentProxy } = await ensureUnpdf();
    const pdf = await getDocumentProxy(bytes);
    const { text } = await extractText(pdf, { mergePages: true });
    const snippet = typeof text === "string" ? text.trim() : "";
    return snippet.length > 0 ? snippet.slice(0, maxChars) : null;
  } catch (err) {
    console.warn("[vault] pdf text snippet error", err);
    return null;
  }
}

/** Authenticated blob URL for native PDF embed/object previews. */
export async function fetchVaultPdfEmbedUrl(item: VaultItem): Promise<string | null> {
  return fetchVaultItemPreviewUrl(item);
}

/** Pull readable cell text from an .xlsx file (shared strings + inline strings). */
export async function extractXlsxSnippet(
  bytes: ArrayBuffer,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const parts: string[] = [];

    const sharedStrings = zip.file("xl/sharedStrings.xml");
    if (sharedStrings) {
      const xml = await sharedStrings.async("string");
      parts.push(...extractXmlTextTags(xml, "t"));
    }

    if (parts.length === 0) {
      const sheet = zip.file(/^xl\/worksheets\/sheet\d+\.xml$/)?.[0];
      if (sheet) {
        const xml = await sheet.async("string");
        parts.push(...extractXmlTextTags(xml, "t"));
      }
    }

    return trimSnippet(parts.join(" "), maxChars);
  } catch (err) {
    console.warn("[vault] xlsx snippet error", err);
    return null;
  }
}

/** Pull readable cell text from a legacy .xls file via SheetJS. */
export async function extractXlsSnippet(
  bytes: ArrayBuffer,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const workbook = XLSX.read(bytes, { type: "array", cellDates: false });
    const parts: string[] = [];
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) continue;
      const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
      if (csv.trim()) parts.push(csv.trim());
      if (parts.join("\n").length >= maxChars) break;
    }
    return trimSnippet(parts.join("\n"), maxChars);
  } catch (err) {
    console.warn("[vault] xls snippet error", err);
    return null;
  }
}

/** Extract readable text from a .docx file (OpenXML zip). */
export async function extractDocxSnippet(
  bytes: ArrayBuffer,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const document = zip.file("word/document.xml");
    if (!document) return null;
    const xml = await document.async("string");
    return trimSnippet(extractXmlTextTags(xml, "w:t").join(" "), maxChars);
  } catch (err) {
    console.warn("[vault] docx snippet error", err);
    return null;
  }
}

/** Extract readable text from a .pptx file (OpenXML zip). */
export async function extractPptxSnippet(
  bytes: ArrayBuffer,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const parts: string[] = [];
    const slideFiles = Object.keys(zip.files)
      .filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name))
      .sort();
    for (const name of slideFiles) {
      const slide = zip.file(name);
      if (!slide) continue;
      const xml = await slide.async("string");
      parts.push(...extractXmlTextTags(xml, "a:t"));
      if (parts.join(" ").length >= maxChars) break;
    }
    return trimSnippet(parts.join(" "), maxChars);
  } catch (err) {
    console.warn("[vault] pptx snippet error", err);
    return null;
  }
}

/** Extract a preview image from an Apple Numbers file when available. */
export async function extractNumbersPreviewUrl(bytes: ArrayBuffer): Promise<string | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const previewPath =
      Object.keys(zip.files).find((name) => /preview\.(jpg|jpeg|png|webp)$/i.test(name)) ?? null;
    if (!previewPath) return null;
    const preview = zip.file(previewPath);
    if (!preview) return null;
    const blob = await preview.async("blob");
    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn("[vault] numbers preview error", err);
    return null;
  }
}

/** List archive contents for zip-based archives. */
export async function extractZipListingSnippet(
  bytes: ArrayBuffer,
  maxEntries = ARCHIVE_LISTING_MAX_ENTRIES,
  maxChars = SNIPPET_MAX_CHARS,
): Promise<string | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const names = Object.keys(zip.files)
      .filter((name) => !zip.files[name]?.dir)
      .sort((a, b) => a.localeCompare(b));
    if (names.length === 0) return null;
    const shown = names.slice(0, maxEntries);
    const lines = shown.map((name) => `• ${name}`);
    if (names.length > shown.length) {
      lines.push(`… +${names.length - shown.length} more`);
    }
    return trimSnippet(lines.join("\n"), maxChars);
  } catch (err) {
    console.warn("[vault] zip listing error", err);
    return null;
  }
}

/** Fetch spreadsheet bytes and return a text snippet for card preview. */
export async function fetchVaultSpreadsheetSnippet(item: VaultItem): Promise<string | null> {
  const mimeType = resolveVaultMimeType(item).toLowerCase();
  const file = await fetchVaultItemAsFile(item);
  if (!file) return null;
  const bytes = await file.arrayBuffer();

  if (
    mimeType === "text/csv" ||
    mimeType === "application/csv" ||
    item.name.toLowerCase().endsWith(".csv")
  ) {
    const text = await file.text();
    const snippet = text.trim();
    return snippet.length > 0 ? snippet.slice(0, SNIPPET_MAX_CHARS) : null;
  }

  if (mimeType === "application/vnd.ms-excel" || item.name.toLowerCase().endsWith(".xls")) {
    return extractXlsSnippet(bytes);
  }

  return extractXlsxSnippet(bytes);
}

/** Fetch office document bytes and return a text or image preview snippet. */
export async function fetchVaultOfficeSnippet(
  item: VaultItem,
): Promise<{ kind: "text"; text: string } | { kind: "image"; url: string } | null> {
  const mimeType = resolveVaultMimeType(item).toLowerCase();
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  const file = await fetchVaultItemAsFile(item);
  if (!file) return null;
  const bytes = await file.arrayBuffer();

  if (
    mimeType.includes("wordprocessing") ||
    extension === "docx" ||
    (mimeType === "application/msword" && extension === "docx")
  ) {
    const text = await extractDocxSnippet(bytes);
    return text ? { kind: "text", text } : null;
  }

  if (
    mimeType.includes("presentation") ||
    extension === "pptx" ||
    (mimeType === "application/vnd.ms-powerpoint" && extension === "pptx")
  ) {
    const text = await extractPptxSnippet(bytes);
    return text ? { kind: "text", text } : null;
  }

  if (mimeType.includes("numbers") || extension === "numbers") {
    const imageUrl = await extractNumbersPreviewUrl(bytes);
    if (imageUrl) return { kind: "image", url: imageUrl };
    const listing = await extractZipListingSnippet(bytes);
    return listing ? { kind: "text", text: listing } : null;
  }

  if (extension === "doc" || mimeType === "application/msword") {
    const text = extractPrintableStrings(bytes);
    return text ? { kind: "text", text } : null;
  }

  if (extension === "ppt" || mimeType === "application/vnd.ms-powerpoint") {
    const text = extractPrintableStrings(bytes);
    return text ? { kind: "text", text } : null;
  }

  return null;
}

/** Fetch archive bytes and return a listing snippet for card preview. */
export async function fetchVaultArchiveSnippet(item: VaultItem): Promise<string | null> {
  const mimeType = resolveVaultMimeType(item).toLowerCase();
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  const file = await fetchVaultItemAsFile(item);
  if (!file) return null;
  const bytes = await file.arrayBuffer();

  if (
    mimeType === "application/zip" ||
    mimeType === "application/x-zip-compressed" ||
    extension === "zip"
  ) {
    return extractZipListingSnippet(bytes);
  }

  // Other archive formats: show extension hint (full listing needs format-specific parsers).
  return `Archive (${extension || mimeType})`;
}

/** Convert HEIC/HEIF bytes to a browser-previewable JPEG blob URL. */
export async function convertHeicPreviewUrl(item: VaultItem): Promise<string | null> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return null;
    const { default: heic2any } = await import("heic2any");
    const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.85 });
    const blob = Array.isArray(result) ? result[0] : result;
    if (!blob) return null;
    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn("[vault] heic conversion error", err);
    return null;
  }
}

/** Unified text snippet fetcher for office, archive, and structured text items. */
export async function fetchVaultTextSnippet(item: VaultItem): Promise<string | null> {
  const mimeType = resolveVaultMimeType(item);

  if (isOfficeFile(item)) {
    const office = await fetchVaultOfficeSnippet(item);
    if (office?.kind === "text") return office.text;
    return null;
  }

  if (isArchiveFile(item)) {
    return fetchVaultArchiveSnippet(item);
  }

  if (isSpreadsheetFile(item)) {
    return fetchVaultSpreadsheetSnippet(item);
  }

  if (
    mimeType.toLowerCase().startsWith("text/") ||
    mimeType.toLowerCase().includes("json") ||
    mimeType.toLowerCase().includes("xml")
  ) {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return null;
    const text = await file.text();
    return text.trim().length > 0 ? text.trim().slice(0, SNIPPET_MAX_CHARS) : null;
  }

  return null;
}
